import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Mail, ImagePlus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr } from "../api";
import { useAuth } from "../AuthContext";
import { RoleBadge, Btn, Field, inputCls, MediaPicker } from "../components";

const BrandingCard = () => {
  const [saved, setSaved] = useState("");
  const [draft, setDraft] = useState("");
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    adminApi.get("/branding").then((r) => { setSaved(r.data.logo_url || ""); setDraft(r.data.logo_url || ""); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  const dirty = draft !== saved;

  const save = async (logoUrl) => {
    setBusy(true);
    try {
      await adminApi.put("/branding", { logo_url: logoUrl });
      setSaved(logoUrl);
      setDraft(logoUrl);
      toast.success(logoUrl ? "Logo updated — the public website now uses it" : "Logo reset to the default G.R mark");
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-sm p-6 space-y-4" data-testid="branding-card">
      <p className="font-display text-sm font-bold text-slate-900 flex items-center gap-2"><ImagePlus size={15} className="text-[#C5221F]" /> Branding — Company Logo</p>
      <p className="text-xs text-slate-500 leading-relaxed">Super Admin only. The logo updates the public header, mobile menu and footer automatically — no rebuild or republish needed. Aspect ratio is always preserved.</p>
      <div className="flex items-center gap-5">
        <div className="h-16 min-w-16 px-2 border border-slate-200 bg-slate-50 flex items-center justify-center">
          {draft ? (
            <img src={draft} alt="Logo preview" className="h-12 w-auto object-contain" data-testid="branding-preview" />
          ) : (
            <span className="bg-[#C5221F] text-white font-display font-extrabold w-11 h-11 flex items-center justify-center" data-testid="branding-default-preview">G.R</span>
          )}
        </div>
        <div className="space-y-2">
          <Btn variant="ghost" onClick={() => setPicker(true)} data-testid="branding-choose-btn"><ImagePlus size={13} /> {draft ? "Choose Another Logo" : "Choose Logo"}</Btn>
          {saved && (
            <button onClick={() => save("")} disabled={busy} className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-500 hover:text-[#C5221F]" data-testid="branding-reset-btn">
              <RotateCcw size={12} /> Reset to Default
            </button>
          )}
        </div>
      </div>
      {dirty && (
        <div className="flex items-center gap-3 border-t border-slate-100 pt-4">
          <Btn onClick={() => save(draft)} disabled={busy} data-testid="branding-save-btn">{busy && <Loader2 size={13} className="animate-spin" />} Save &amp; Publish Logo</Btn>
          <Btn variant="ghost" onClick={() => setDraft(saved)} data-testid="branding-cancel-btn">Cancel</Btn>
          <span className="text-[11px] text-amber-600 font-semibold">Unsaved preview — not yet public</span>
        </div>
      )}
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(url) => setDraft(url)} />
    </div>
  );
};

const Settings = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [recruitEmail, setRecruitEmail] = useState("");
  const [recruitBusy, setRecruitBusy] = useState(false);

  useEffect(() => {
    if (user.role === "super_admin" || user.role === "hr") {
      adminApi.get("/settings/recruitment-email").then((r) => setRecruitEmail(r.data.email)).catch(() => {});
    }
  }, [user.role]);

  const saveRecruitEmail = async (e) => {
    e.preventDefault();
    setRecruitBusy(true);
    try {
      await adminApi.put("/settings/recruitment-email", { email: recruitEmail });
      toast.success("Recruitment notification email updated");
    } catch (err) { toast.error(fmtErr(err)); } finally { setRecruitBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-settings-page">
      <h1 className="font-display text-2xl font-bold text-slate-900">Settings</h1>
      <p className="mt-1 text-sm text-slate-500">Your account and security preferences.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-sm p-6">
        <p className="font-display text-sm font-bold text-slate-900">Your Account</p>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">{user.name}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
          <RoleBadge role={user.role} />
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <p className="font-display text-sm font-bold text-slate-900 flex items-center gap-2"><ShieldCheck size={15} className="text-[#C5221F]" /> Change Password</p>
        <Field label="Current Password">
          <input type="password" className={inputCls} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required autoComplete="current-password" data-testid="current-password-input" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="New Password" hint="Minimum 10 characters.">
            <input type="password" className={inputCls} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required minLength={10} autoComplete="new-password" data-testid="new-password-input" />
          </Field>
          <Field label="Confirm New Password">
            <input type="password" className={inputCls} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required minLength={10} autoComplete="new-password" data-testid="confirm-password-input" />
          </Field>
        </div>
        <Btn type="submit" disabled={busy} data-testid="change-password-btn">{busy && <Loader2 size={13} className="animate-spin" />} Update Password</Btn>
      </form>

      {(user.role === "super_admin" || user.role === "hr") && (
        <form onSubmit={saveRecruitEmail} className="mt-6 bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <p className="font-display text-sm font-bold text-slate-900 flex items-center gap-2"><Mail size={15} className="text-[#C5221F]" /> Recruitment Notifications</p>
          <Field label="Job Application Alerts Go To" hint="Every new career application is emailed to this address. Resumes are never attached — they stay secured in the CMS.">
            <input type="email" className={inputCls} value={recruitEmail} onChange={(e) => setRecruitEmail(e.target.value)} required data-testid="recruitment-email-input" />
          </Field>
          <Btn type="submit" disabled={recruitBusy} data-testid="recruitment-email-save-btn">{recruitBusy && <Loader2 size={13} className="animate-spin" />} Save</Btn>
        </form>
      )}

      {user.role === "super_admin" && <BrandingCard />}

      <div className="mt-6 bg-slate-50 border border-slate-200 rounded-sm p-5">
        <p className="text-xs text-slate-500 leading-relaxed">
          Website domain, hosting and deployment configuration are managed outside this panel and are not editable here — this protects the live site from accidental changes.
        </p>
      </div>
    </div>
  );
};

export default Settings;
